import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Avatar, Badge, Button, Card, Coin, Rating } from '@/components/ui';
import ServiceIcon, { BookingTypeIcon, categoryTint } from '@/components/companion/ServiceIcon';
import CityPicker from '@/components/forms/CityPicker';
import {
  useServiceCategories,
  useServiceListings,
  useSelectedCity,
  bookingTypeLabel,
  unitLabel,
  isRemote,
  cheapestRate,
  type BookingType,
  type ServiceListing,
} from '@/lib/data/services';
import { hueFor, initialsFor } from '@/lib/data/companions';

/** Everyone offering one service — project.md §11. */
export default function ServiceBrowse() {
  const { slug } = useParams<{ slug: string }>();
  const { categories } = useServiceCategories();
  const [city, setCity] = useSelectedCity();
  const [bookingType, setBookingType] = useState<BookingType | null>(null);

  const { listings, loading, error } = useServiceListings(slug, {
    city,
    bookingType,
    maxPrice: null,
  });

  const category = categories.find((c) => c.slug === slug);
  const tint = categoryTint(category?.hue ?? 210);

  // City only narrows anything when an in-person booking is possible. For a
  // remote-only service the control would be a filter that filters nothing.
  const cityMatters = category
    ? category.allowedBookingTypes.includes('in_person') &&
      (bookingType === null || bookingType === 'in_person')
    : false;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <Link
        to="/services"
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-[var(--ink-2)] transition-colors hover:text-[var(--ink)]"
      >
        ← All services
      </Link>

      <header className="animate-rise mb-6 flex items-start gap-4">
        <span
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl"
          style={tint}
        >
          <ServiceIcon iconKey={category?.iconKey ?? 'circle'} size={28} />
        </span>

        <div>
          <h1 className="text-3xl font-semibold">{category?.name ?? 'Service'}</h1>
          {category && (
            <p className="mt-1.5 max-w-lg text-[var(--ink-2)]">{category.description}</p>
          )}
        </div>
      </header>

      {/* Filters */}
      {category && (
        <div className="animate-rise mb-8 flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-[var(--ink-2)]">How</span>
            <FilterChip active={bookingType === null} onClick={() => setBookingType(null)}>
              Any
            </FilterChip>
            {category.allowedBookingTypes.map((t) => (
              <FilterChip
                key={t}
                active={bookingType === t}
                onClick={() => setBookingType(bookingType === t ? null : t)}
              >
                <BookingTypeIcon type={t} size={14} />
                {bookingTypeLabel(t)}
              </FilterChip>
            ))}
          </div>

          {cityMatters && <CityPicker value={city} onChange={setCity} />}
        </div>
      )}

      {error && (
        <Card className="px-5 py-4">
          <p className="text-sm text-[var(--ink-2)]">Could not load listings. {error}</p>
        </Card>
      )}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-56 animate-pulse rounded-2xl"
              style={{ background: 'var(--surface-2)' }}
            />
          ))}
        </div>
      ) : listings.length === 0 ? (
        <Empty
          cityFiltered={cityMatters && Boolean(city)}
          onClearCity={() => setCity(null)}
          onClearType={() => setBookingType(null)}
          typeFiltered={bookingType !== null}
        />
      ) : (
        <>
          <p className="mb-4 text-sm text-[var(--ink-2)]">
            {listings.length} {listings.length === 1 ? 'companion' : 'companions'} available
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {listings.map((l, i) => (
              <ListingCard key={l.id} listing={l} index={i} highlight={bookingType} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function FilterChip({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-150 hover:-translate-y-px"
      style={
        active
          ? { background: 'var(--marigold)', color: '#fff' }
          : {
              background: 'var(--surface)',
              color: 'var(--ink-2)',
              border: '1px solid var(--line)',
            }
      }
    >
      {children}
    </button>
  );
}

function ListingCard({
  listing,
  index,
  highlight,
}: {
  listing: ServiceListing;
  index: number;
  highlight: BookingType | null;
}) {
  // When a channel is filtered on, show that price. Otherwise show the
  // cheapest, so the card answers "what does this cost me at minimum".
  const shown = highlight
    ? (listing.rates.find((r) => r.bookingType === highlight) ?? cheapestRate(listing.rates))
    : cheapestRate(listing.rates);

  return (
    <Card
      as="li"
      className="animate-rise group flex flex-col transition-all duration-200 hover:-translate-y-0.5"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <Link to={`/companions/${listing.companionId}`} className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-center gap-3">
          <Avatar
            initials={initialsFor(listing.displayName) || 'C'}
            hue={hueFor(listing.companionId)}
            photo={listing.avatarUrl ?? undefined}
            alt={listing.displayName}
            size={48}
          />
          <div className="min-w-0">
            <h3 className="truncate font-semibold transition-colors group-hover:text-[var(--marigold)]">
              {listing.displayName}
            </h3>
            {listing.ratingCount > 0 ? (
              <Rating value={listing.rating} count={listing.ratingCount} />
            ) : (
              <span className="text-xs text-[var(--ink-2)]">New</span>
            )}
          </div>
        </div>

        {listing.headline && (
          <p className="line-clamp-2 text-sm leading-relaxed text-[var(--ink-2)]">
            {listing.headline}
          </p>
        )}

        <div className="flex flex-wrap gap-1.5">
          {listing.rates.map((r) => (
            <Badge key={r.bookingType} tone={r.bookingType === highlight ? 'marigold' : 'neutral'}>
              {bookingTypeLabel(r.bookingType)}
            </Badge>
          ))}
          {listing.cityName && listing.rates.some((r) => !isRemote(r.bookingType)) && (
            <Badge tone="teal">{listing.cityName}</Badge>
          )}
        </div>

        <div className="mt-auto flex items-center justify-between pt-2">
          {shown ? (
            <span className="flex items-baseline gap-1.5 font-semibold">
              <Coin />
              {shown.priceCoins}
              <span className="text-sm font-normal text-[var(--ink-2)]">
                {unitLabel(shown.unit)}
              </span>
            </span>
          ) : (
            <span className="text-sm text-[var(--ink-2)]">Price on request</span>
          )}
          <span className="text-sm text-[var(--marigold)]">View →</span>
        </div>
      </Link>
    </Card>
  );
}

function Empty({
  cityFiltered,
  typeFiltered,
  onClearCity,
  onClearType,
}: {
  cityFiltered: boolean;
  typeFiltered: boolean;
  onClearCity: () => void;
  onClearType: () => void;
}) {
  const filtered = cityFiltered || typeFiltered;

  return (
    <Card className="animate-rise px-6 py-14 text-center">
      <h2 className="text-lg font-semibold">
        {filtered ? 'Nothing matches those filters' : 'Nobody offers this yet'}
      </h2>
      <p className="mx-auto mt-2 max-w-sm text-sm text-[var(--ink-2)]">
        {filtered
          ? 'Try widening your search — there may be companions available another way.'
          : 'We are onboarding companions for this service now. Check back soon.'}
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        {typeFiltered && (
          <Button variant="secondary" onClick={onClearType}>
            Any booking type
          </Button>
        )}
        {cityFiltered && (
          <Button variant="secondary" onClick={onClearCity}>
            All cities
          </Button>
        )}
        <Button to="/services" variant={filtered ? 'ghost' : 'primary'}>
          Browse other services
        </Button>
      </div>
    </Card>
  );
}
